/**
 =========================================================
 * Soft UI Dashboard PRO React - v2.0.0
 =========================================================
 
 * Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
 * Copyright 2021 Creative Tim (https://www.creative-tim.com)
 
 Coded by www.creative-tim.com
 
 =========================================================
 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

/**
 All of the routes for the page layout of Soft UI Dashboard PRO Material are added here,
 You can add a new route, customize the routes and delete the routes here.
 
 Once you add a new route on this file it will be visible automatically on
 the DefaultNavbar.
 
 For adding a new route you can follow the existing routes in the routes array.
 1. The `type` key with the `collapse` value is used for a route.
 2. The `name` key is used for the name of the route on the DefaultNavbar.
 3. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
 4. The `icon` key is used for the icon of the route on the DefaultNavbar, you have to add a node.
 5. The `collapse` key is used for making a collapsible item on the DefaultNavbar that has other routes
 nside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
 6. The `route` key is used to store the route location which is used for the react router.
 7. The `href` key is used to store the external links location.
 */

// Components
import { AssessmentOutlined, PowerInput } from "@mui/icons-material";
import SettingsIcon from "components/Icons/Settings";
import Shop from "components/Icons/Shop";
import SpaceShip from "components/Icons/SpaceShip";

const navRoutes = (t, ability, communityId, communityType) => {
  const arr = [
    {
      type: "collapse",
      name: "Dashboard",
      key: `${communityId}`,
      route: `/communities/${communityId}`,
      icon: <SpaceShip size="12px" />,
      noCollapse: true,
      isAllowed: ability.can("read", "metrics"),
    },
    {
      type: "title",
      title: t("generalinfo"),
      key: "title-general-info",
      isAllowed: ability.can("read", "generalInfo"),
    },
    {
      type: "collapse",
      name: t("community"),
      key: "community",
      icon: "business",
      isAllowed: ability.can("manage", "all"),
      collapse: [
        {
          name: t("config"),
          key: `configuration`,
          route: `/communities/${communityId}/configuration`,
          isAllowed: ability.can("manage", "all"),
        },
        {
          name: t("fixedCoefficients"),
          key: `coefficients`,
          route: `/communities/${communityId}/coefficients`,
          isAllowed: ability.can("manage", "all"),
        },
        {
          name: t("bilateralPrices"),
          key: `bilateral-prices`,
          route: `/communities/${communityId}/bilateral-prices`,
          isAllowed: ability.can("manage", "all"),
        },
        {
          name: t("opportunitycosts"),
          key: `opportunity_costs`,
          route: `/communities/${communityId}/opportunity_costs`,
          isAllowed: ability.can("manage", "all"),
        },
      ],
    },
    {
      type: "collapse",
      name: t("members"),
      key: "members",
      route: `/communities/${communityId}/members`,
      icon: "account_circle",
      noCollapse: true,
      isAllowed: ability.can("manage", "all"),
    },
    {
      type: "collapse",
      name: t("infrastructure"),
      key: "infrastructure",
      icon: <SettingsIcon size="12px" />,
      isAllowed: ability.can("read", "infrastructure"),
      collapse: [
        {
          name: t("resources"),
          key: "resources",
          route: `/communities/${communityId}/infrastructure/resources`,
          isAllowed: ability.can("read", "infrastructure"),
        },
        {
          name: t("meters"),
          key: "meters",
          route: `/communities/${communityId}/infrastructure/meters`,
          isAllowed: ability.can("read", "infrastructure"),
        },
        {
          name: t("contracts", { defaultValue: "Contratos" }),
          key: "contracts",
          route: `/communities/${communityId}/infrastructure/contracts`,
          isAllowed: ability.can("read", "infrastructure"),
        },
        {
          name: t("resp"),
          key: "resp",
          route: `/communities/${communityId}/infrastructure/resp`,
          isAllowed: ability.can("manage", "all"),
        },
      ],
    },
    {
      type: "title",
      title: t("external"),
      key: "title-external-info",
      isAllowed: ability.can("read", "OMIE"),
    },
    {
      type: "collapse",
      name: t("omie"),
      key: "omie",
      icon: "public",
      route: `/communities/${communityId}/omie`,
      noCollapse: true,
      isAllowed: ability.can("read", "OMIE"),
    },
    {
      type: "title",
      title: t("operations"),
      key: "title-operations",
      isAllowed: ability.can("read", "loads") || ability.can("manage", "all"),
    },
    {
      type: "collapse",
      name: t("internalloads"),
      key: "loads_collapse",
      icon: "electric_meter",
      isAllowed: ability.can("read", "loads"),
      collapse: [
        {
          name: t("measurements"),
          key: `measurements`,
          route: `/communities/${communityId}/loads/measurements`,
          isAllowed: ability.can("read", "loads"),
        },
        {
          name: t("ev"),
          key: `ev`,
          route: `/communities/${communityId}/loads/ev`,
          isAllowed: ability.can("read", "loads"),
        },
        {
          name: t("batteries"),
          key: `batteries`,
          route: `/communities/${communityId}/loads/batteries`,
          isAllowed: ability.can("read", "loads"),
        },
        {
          name: t("ewh_usage"),
          key: `ewh_usage`,
          route: `/communities/${communityId}/loads/ewh_usage`,
          isAllowed: ability.can("read", "loads"),
        },
        {
          name: t("hp"),
          key: `hp`,
          route: `/communities/${communityId}/loads/hp`,
          isAllowed: ability.can("read", "loads"),
        },
      ],
    },

    {
      type: "collapse",
      name: t("dso"),
      key: "dso",
      route: `/communities/${communityId}/dso`,
      icon: <PowerInput size="12px" />,
      isAllowed: ability.can("manage", "all"),
      noCollapse: true,
    },
    {
      type: "collapse",
      name: t("energymanagement"),
      key: "energy_management",
      route: `/communities/${communityId}/energymanagement`,
      icon: "electric_bolt",
      noCollapse: true,
      isAllowed: ability.can("manage", "all"),
    },
    {
      type: "collapse",
      name: t("market"),
      key: "market",
      icon: <Shop size="12px" />,
      isAllowed: ability.can("read", "market"),
      route: `/communities/${communityId}/market`,
      noCollapse: true,
    },
    {
      type: "collapse",
      name: t("settlement"),
      key: "settlement",
      icon: "euro",
      isAllowed: ability.can("read", "settlement"),
      route: `/communities/${communityId}/settlement`,
      noCollapse: true,
    },
  ];

  if (communityType === "DEMO") {
    // Find the "operations" title index
    const operationsIndex = arr.findIndex((item) => item.key === "title-operations");

    // Insert Simulação after the operations title (operationsIndex + 1)
    arr.splice(operationsIndex + 1, 0, {
      type: "collapse",
      name: t("simulation"),
      key: "simulation",
      route: `/communities/${communityId}/simulation`,
      icon: <AssessmentOutlined size="12px" />,
      noCollapse: true,
      isAllowed: ability.can("read", "metrics") || ability.can("manage", "all"),
    });
  }

  if (communityType !== "DEMO") {
    // Find the "operations" title index
    const operationsIndex = arr.findIndex((item) => item.key === "title-operations");

    // Insert Run Operations after the operations title
    arr.splice(operationsIndex + 1, 0, {
      type: "collapse",
      name: t("runoperations"),
      key: "run_operations",
      route: `/communities/${communityId}/operations/run`,
      icon: <AssessmentOutlined size="12px" />,
      noCollapse: true,
      isAllowed: ability.can("manage", "all"),
    });
  }

  return arr;
};

export default navRoutes;
